import React, { Component, Suspense } from 'react';
import { App, f7, View } from 'framework7-react';
import StartFromQR from './components/start/StartFromQR';
import RequestedDocuments from './components/requestedDocuments/RequestedDocumentsContainer';
import Thankyou from './components/ThankyouContainer';
import Load from './components/LoadContainer';
import {
  getBrowserVersion,
  getRequestInfoFromUrl,
  isDesktopMode,
  isMobileOrTablet,
  isRequestInTestMode,
  isTouchDevice,
  prepareApp,
  initOnlinePrepareListener,
  REQUEST_TYPE
} from './lib/utils';
import {
  checkDict,
  localeLangToDictionary
} from './components/translate/dictionary';
import { PATHS } from './components/helpers/navigator';
import II18 from './components/translate/II18';
import { importWithRetry, initImportWithRetry } from './lib/importWithRetry';
import { confirmDialog } from './components/helpers/dialogUtils';
import Spinner from './components/helpers/Spinner';
import PropTypes from 'prop-types';
import withExternalPopupWindow from './components/helpers/withExternalPopupWindow';
import {
  EXTERNAL_POPUP_WINDOW_STATUS,
  setActualScreenSize,
  setCurrentRequestPageIndex,
  setLandscapeMode,
  setVirtualKeyboardOpen
} from './redux/actions/rootAction';
import {
  APP_EXTERNAL_EVENTS,
  defineScreenSize,
  getIcon,
  getViewPortHeight
} from './components/helpers/utils';
import ErrorBoundary from './components/helpers/ErrorBoundary';
import {
  gaeApp,
  gaeError,
  ensurePaperflyOtel
} from './components/helpers/Analytics';
import {
  InjectCookieBanner,
  ShowCookieBanner
} from './components/home/Elements';
import detectBrowserLanguage from 'detect-browser-language';
import { create_UUID } from './redux/utils';
import { xconnect } from './redux/xconnect';
import RequestOverlays from './components/common/RequestOverlays/RequestOverlays';

const requestInfo = getRequestInfoFromUrl();

window.pwaSession = create_UUID('xxxxxxxx');
window.browserVer = getBrowserVersion();
// Parent home-screen / installed PWA can make an iframe inherit
// display-mode:standalone. That turns on StartFromQR/draft UX and
// fights embedMode — so embed=true always forces browser product mode.
// SW update checks still run if a worker already controls the page
// (see shouldBootstrapServiceWorker).
window.standalone =
  (window.matchMedia('(display-mode: standalone)').matches ||
    // iOS home-screen (Safari) uses this legacy flag
    window.navigator.standalone === true ||
    Boolean(requestInfo.params?.standalone)) &&
  requestInfo.params?.embed !== 'true';
// IndexedDB template draft: standalone /t= or embed /t= (not /d=).
// Standalone without /t= at boot may flip draftEnabled later in StartFromQR.
const isTemplateUrl = requestInfo.requestType === REQUEST_TYPE.TEMPLATE;
const isEmbedUrl = requestInfo.params?.embed === 'true';
window.embedDraftEnabled = isEmbedUrl && isTemplateUrl;
window.draftEnabled = (window.standalone || isEmbedUrl) && isTemplateUrl;
window.isDesktop = !isMobileOrTablet();
window.isDesktopMode = isDesktopMode();
window.isTouchDevice = isTouchDevice();
window.isChrome =
  !!window.chrome && (!!window.chrome.webstore || !!window.chrome.csi);
const chromeVersion = parseInt(
  window.browserVer ? window.browserVer.slice(7) : 999
);
window.chromeVersion =
  window.isChrome && !isNaN(chromeVersion) ? chromeVersion : 999;
window.isSafari =
  navigator.vendor &&
  navigator.vendor.indexOf('Apple') > -1 &&
  navigator.userAgent &&
  navigator.userAgent.indexOf('CriOS') === -1 &&
  navigator.userAgent.indexOf('FxiOS') === -1;

// pwa.session timeline — independent of cookie banner
ensurePaperflyOtel(requestInfo.requestId);

if (isRequestInTestMode(requestInfo)) {
  window.testMode = true;
}

window.userLang = localeLangToDictionary(
  requestInfo.params?.lang || detectBrowserLanguage()
);
document.documentElement.lang = detectBrowserLanguage();

const appPlatform = window.isDesktop ? 'desktop' : 'mobile';

const f7params = {
  name: 'Paperfly',
  id: 'io.paperfly.client',
  material: true,
  init: false,
  touch: {
    tapHold: true
  },
  on: {
    init: function() {
      initImportWithRetry(doRestart =>
        confirmDialog(
          II18.get('restart_app_title'),
          II18.get('restart_app_message'),
          doRestart,
          null,
          II18.get('restart_app_yes'),
          II18.get('restart_app_no'),
          !window.isDesktop,
          'dialogTopLevel'
        )
      );
    }
  },
  // specify routes for app
  routes: [
    {
      path: '/' + PATHS.SCAN_QR,
      component: StartFromQR
    },
    {
      path: '/' + PATHS.FILE_DOWNLOAD,
      asyncComponent: importWithRetry(() => import('./components/FileDownload'))
    },
    {
      path: '/' + PATHS.SIGN_NOW,
      asyncComponent: importWithRetry(() =>
        import('./components/signNow/SignNow')
      )
    },
    {
      path: '/' + PATHS.IMAGE_VIEWER + '/:docId',
      asyncComponent: importWithRetry(() =>
        import('./components/gallery/mobile/ImageViewerContainer')
      )
    },
    {
      path: '/' + PATHS.PAYMENT + '/:docId',
      asyncComponent: importWithRetry(() =>
        import(`./components/payment/${appPlatform}/Payment`)
      )
    },
    {
      path: '/' + PATHS.VIEWER + '/:docId',
      asyncComponent: importWithRetry(() =>
        import('./components/viewer/ViewerContainer')
      )
    },
    {
      path: '/' + PATHS.PDF_FORM + '/:docId',
      asyncComponent: importWithRetry(() =>
        import(`./components/pdf/${appPlatform}/PDFForm`)
      ),
      beforeLeave: function(to, from, resolve, reject) {
        if (window.disablePDFBack) {
          window.disablePDFBack = false;
          reject();
          return;
        }
        if (to.path === '/PDFForm/date/') {
          window.disablePDFBack = true;
        }
        resolve();
      }
    },
    {
      path: '/' + PATHS.ID_CHECK + '/:docId',
      asyncComponent: importWithRetry(() =>
        import(`./components/idCheck/${appPlatform}/IdCheck`)
      )
    },
    {
      path: '/' + PATHS.QUICK_FORM + '/:docId',
      asyncComponent: importWithRetry(() =>
        import(`./components/quickForm/${appPlatform}/QuickForm`)
      )
    },
    {
      path: '/' + PATHS.IMAGE_FILTER + '/:docId',
      asyncComponent: importWithRetry(() =>
        import('./components/gallery/mobile/ImageFilterContainer')
      )
    },
    {
      path: '/' + PATHS.THANKYOU,
      component: Thankyou
    },
    {
      path: '/' + PATHS.GALLERY + '/:docId',
      asyncComponent: importWithRetry(() =>
        import(`./components/gallery/${appPlatform}/Gallery`)
      )
    },
    {
      path: '/' + PATHS.HOME,
      asyncComponent: !window.isDesktop
        ? importWithRetry(() => import('./components/home/HomeMobileContainer'))
        : importWithRetry(() =>
            import('./components/home/HomeDesktopContainer')
          )
    },
    {
      path: '/' + PATHS.LIST,
      component: RequestedDocuments
    },
    {
      path: '/' + PATHS.IMAGE_CROPPER + '/:docId',
      asyncComponent: importWithRetry(() =>
        import('./components/gallery/mobile/ImageCropperContainer')
      )
    },
    {
      path: '/' + PATHS.LOAD,
      component: Load
    },
    {
      path: '(.*)',
      // Standalone: /t= stays on StartFromQR (launch prompt); other deep links
      // (e.g. /r=) use Load — same common path as browser.
      component:
        (!requestInfo.requestId && !window.isDesktop) ||
        (window.standalone &&
          (!requestInfo.requestId ||
            requestInfo.requestType === REQUEST_TYPE.TEMPLATE))
          ? StartFromQR
          : Load,
      options: {
        props: { requestInfo }
      }
    }
  ]
};

class PwaScanApp extends Component {
  constructor(props) {
    super(props);
    this.actualViewPortHeight = getViewPortHeight();
  }

  componentDidMount() {
    checkDict();

    const doOnError = () => {
      f7.dialog.close();
      f7.dialog
        .create({
          text: II18.get('technical_error'),
          cssClass: 'no-app-dialog'
        })
        .open();
    };

    prepareApp(() => f7.init(), doOnError);
    initOnlinePrepareListener();

    if (!window.isDesktop) {
      setInterval(this.detectScreenSizeChanges, 100);
    }

    window.addEventListener('message', this.onRecievedExternalMessage);
    window.addEventListener('unhandledrejection', event => {
      gaeError(`UNHANDLED PROMISE REJECTION: ${event.reason}`);
    });
    InjectCookieBanner();
  }

  onRecievedExternalMessage = event => {
    if (event?.data === 'PWA_EXT_CONTROL_NAVIGATE_PREV') {
      this.props.sendSignal(APP_EXTERNAL_EVENTS.APP_PREV_ACTION);
    }
    if (event?.data === 'PWA_EXT_CONTROL_NAVIGATE_NEXT') {
      this.props.sendSignal(APP_EXTERNAL_EVENTS.APP_NEXT_ACTION);
    }
  };

  detectScreenSizeChanges = () => {
    const actualScreenSize = defineScreenSize();

    if (
      this.props.actualScreenSize.width !== actualScreenSize.width ||
      this.props.actualScreenSize.height !== actualScreenSize.height
    ) {
      this.props.setActualScreenSize(actualScreenSize);
    }

    const isLandscape = actualScreenSize.width > actualScreenSize.height;
    if (this.props.isLandscape !== isLandscape) {
      this.actualViewPortHeight = getViewPortHeight();
      this.props.setLandscapeMode(isLandscape);
    } else {
      const isVirtualKeyboardOpen =
        this.actualViewPortHeight > getViewPortHeight();
      if (isVirtualKeyboardOpen !== this.props.isVirtualKeyboardOpen) {
        this.props.setVirtualKeyboardOpen(isVirtualKeyboardOpen);
      }
    }
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.appCrashed) {
      throw new Error('Redux fatal error.', { cause: this.props.appCrashed });
    }
  }

  reloadApp = f7 => {
    gaeApp('RELOAD_APP_AFTER_CRASH');
    let actions, popup, sheet;
    do {
      actions = document.getElementById('uploader_actions');
      popup = document.querySelector('.popup.modal-in');
      sheet = document.querySelector('.sheet-modal');

      if (actions) {
        f7.actions.close();
      } else if (popup) {
        f7.popup.close();
      } else if (sheet) {
        f7.sheet.close();
      }
    } while (actions || popup || sheet);
    this.props.resetCurrentRequestPageIndex();
  };

  render() {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <App params={f7params}>
          <ShowCookieBanner queryRequestId={this.props.recipientRequestId} />

          <ErrorBoundary onReloadApp={() => this.reloadApp(f7)}>
            <div id={'calendarContainer'}></div>
            <div id={'commonPortal'}></div>
            {window.testMode && <div className={'testMode'}></div>}

            <View
              className={
                (this.props.isLandscape ? 'landscape ' : '') +
                (this.props.isVirtualKeyboardOpen ? ' virtualKeyboardOpen' : '')
              }
              main
              pushState={false}
              pushStateSeparator=""
              pushStateRoot=""
              iosSwipeBack={false}
              mdSwipeBack={false}
            />
            <RequestOverlays />
            {(process.env.REACT_APP_ENV === 'dev' ||
              (process.env.REACT_APP_ENV === 'pre-prod' &&
                requestInfo?.params?.debug) ||
              process.env.REACT_APP_ENV === 'local' ||
              process.env.REACT_APP_ENV === 'demo' ||
              process.env.REACT_APP_ENV === 'feature') && (
              <>
                <div
                  id={'pwa_debug'}
                  onClick={() => {
                    document.getElementById('pwa_debug_log').style.display =
                      document.getElementById('pwa_debug_log').style.display ===
                      'block'
                        ? 'none'
                        : 'block';
                    document.getElementById('pwa_debug_log').innerHTML =
                      '<pre>' + window.globalLog + '</pre>';
                  }}
                  style={{
                    position: 'fixed',
                    opacity: 0.3,
                    zIndex: 30001,
                    bottom: 15,
                    left: 5,
                    height: 15,
                    width: 15
                  }}
                >
                  {getIcon('f7:ant_circle_fill')}
                </div>
                <div
                  id={'pwa_debug_log'}
                  style={{
                    paddingBottom: 100,
                    display: 'none',
                    fontSize: 10,
                    background: '#fff',
                    position: 'fixed',
                    zIndex: 30000,
                    top: 0,
                    left: 0,
                    height: '100vh',
                    width: '100vw',
                    overflow: 'scroll'
                  }}
                ></div>
              </>
            )}
            {this.props.externalPopUpStatus !==
              EXTERNAL_POPUP_WINDOW_STATUS.CLOSE && (
              <Spinner id={'externalPopupSpinner'} fullScreen />
            )}
          </ErrorBoundary>
        </App>
      </Suspense>
    );
  }
}

const mapStateToProps = state => {
  return {
    recipientRequestId: state.recipientRequestId,
    selectedLang: state.selectedLang,
    appCrashed: state.appCrashed,
    restoredAfterCrash: state.restoredAfterCrash,
    isLandscape: state.isLandscape,
    actualScreenSize: state.actualScreenSize,
    isVirtualKeyboardOpen: state.isVirtualKeyboardOpen
  };
};

const mapDispatchToProps = dispatch => ({
  setLandscapeMode: mode => dispatch(setLandscapeMode(mode)),
  setVirtualKeyboardOpen: open => dispatch(setVirtualKeyboardOpen(open)),
  setActualScreenSize: size => dispatch(setActualScreenSize(size)),
  resetCurrentRequestPageIndex: () => dispatch(setCurrentRequestPageIndex(0))
});

PwaScanApp.propTypes = {
  restoredAfterCrash: PropTypes.bool,
  recipientRequestId: PropTypes.string,
  appCrashed: PropTypes.any,
  externalPopUpStatus: PropTypes.any,
  selectedLang: PropTypes.string,
  isVirtualKeyboardOpen: PropTypes.bool,
  isLandscape: PropTypes.bool,
  resetCurrentRequestPageIndex: PropTypes.func,
  setLandscapeMode: PropTypes.func,
  setVirtualKeyboardOpen: PropTypes.func,
  setActualScreenSize: PropTypes.func,
  sendSignal: PropTypes.func,
  actualScreenSize: PropTypes.object
};

export default xconnect(
  mapStateToProps,
  mapDispatchToProps
)(withExternalPopupWindow(PwaScanApp));
